import { useCallback, useMemo, useRef, useState } from 'react'
import type { HTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'

import { useWheelScrollProxy } from '#/shared/lib/hooks'
import { cn } from '#/shared/lib/utils'
import { ChevronDownIcon } from '#/shared/ui/icons'
import { Label } from '#/shared/ui/label'
import type { LabelSize } from '#/shared/ui/label'
import { Popover } from '#/shared/ui/popover'
import { Skeleton } from '#/shared/ui/skeleton'

export interface SelectOption {
  value: string
  label: string
}

export interface SelectProps {
  options: SelectOption[]
  value?: string
  onValueChange?: (value: string) => void
  onOpenChange?: (open: boolean) => void
  loading?: boolean
  placeholder?: string
  disabled?: boolean
  error?: boolean
  label?: string
  labelSize?: LabelSize
  selectSize?: 'default' | 'sm'
  align?: 'left' | 'center' | 'right'
  className?: string
  /** Props for the option-list panel — e.g. `data-phone-surface` for theming a
   *  portaled list that sits outside a scoped-theme subtree. */
  contentProps?: HTMLAttributes<HTMLDivElement> & {
    [key: `data-${string}`]: string | undefined
  }
  id?: string
  portal?: boolean
}

const SKELETON_ROW_COUNT = 3

const sizeClasses = {
  default: 'h-12 px-2.5 text-base',
  sm: 'h-8 px-2 text-sm',
} as const

function SelectTrigger({
  open,
  disabled,
  error,
  selectSize,
  className,
  children,
}: {
  open: boolean
  disabled: boolean
  error: boolean
  selectSize: 'default' | 'sm'
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      data-slot="select-trigger"
      className={cn(
        'flex w-full cursor-pointer items-center justify-between gap-1 rounded-sm border bg-white transition-colors',
        sizeClasses[selectSize],
        error
          ? 'border-red-500 focus-within:focus-ring-error'
          : 'border-gray-200 focus-within:focus-ring',
        disabled && 'cursor-not-allowed opacity-50',
        open && !error && 'focus-ring',
        className,
      )}
    >
      {children}
      <ChevronDownIcon
        className={cn(
          'size-2.5 shrink-0 text-gray-500 transition-transform duration-200',
          open && 'rotate-180',
        )}
      />
    </div>
  )
}

function SelectSkeletonRows() {
  return (
    <>
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
        <div key={i} className="flex h-8 items-center px-2.5">
          <Skeleton className="h-3 w-24" />
        </div>
      ))}
    </>
  )
}

function SelectPanel({
  options,
  selectedValue,
  loading,
  onSelect,
  contentProps,
}: {
  options: SelectOption[]
  selectedValue?: string
  loading: boolean
  onSelect: (option: SelectOption) => void
  contentProps?: HTMLAttributes<HTMLDivElement>
}) {
  const { t } = useTranslation()
  const scrollRef = useRef<HTMLDivElement>(null)
  // Keep wheel-scrolling alive when the panel is portaled inside a Radix dialog,
  // whose scroll lock otherwise eats wheel events on out-of-tree content.
  useWheelScrollProxy(scrollRef)

  return (
    <div
      {...contentProps}
      ref={scrollRef}
      className={cn(
        'flex max-h-60 w-max min-w-full flex-col gap-0.5 overflow-y-auto rounded-md bg-white p-1 shadow-popover whitespace-nowrap',
        contentProps?.className,
      )}
    >
      {loading ? (
        <SelectSkeletonRows />
      ) : options.length === 0 ? (
        <div className="flex h-8 items-center px-2.5 text-sm text-gray-500">
          {t('select.empty')}
        </div>
      ) : (
        options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onSelect(option)}
            className={cn(
              'flex h-8 w-full shrink-0 cursor-pointer items-center rounded-sm px-2.5 text-left text-sm whitespace-nowrap text-gray-900 hover:bg-gray-100',
              option.value === selectedValue && 'bg-gray-100',
            )}
          >
            {option.label}
          </button>
        ))
      )}
    </div>
  )
}

function useControlledSelect(
  value: string | undefined,
  onValueChange?: (value: string) => void,
) {
  const [internalValue, setInternalValue] = useState(value)
  const isControlled = value !== undefined
  const currentValue = isControlled ? value : internalValue
  const setValue = useCallback(
    (next: string) => {
      if (!isControlled) setInternalValue(next)
      onValueChange?.(next)
    },
    [isControlled, onValueChange],
  )
  return { currentValue, setValue }
}

function SelectTriggerContent({
  selectedOption,
  placeholder,
}: {
  selectedOption?: SelectOption
  placeholder: string
}) {
  return (
    <div className="w-full min-w-0 flex-1 truncate">
      {selectedOption ? (
        <span className="text-gray-900">{selectedOption.label}</span>
      ) : (
        <span className="text-gray-300">{placeholder}</span>
      )}
    </div>
  )
}

export function Select({
  options,
  value,
  onValueChange,
  onOpenChange,
  loading = false,
  placeholder,
  disabled = false,
  error = false,
  label,
  labelSize = 'default',
  selectSize = 'sm',
  align = 'left',
  className,
  contentProps,
  id,
  portal,
}: SelectProps) {
  const { t } = useTranslation()
  const { currentValue, setValue } = useControlledSelect(value, onValueChange)
  const selectedOption = useMemo(
    () => options.find((o) => o.value === currentValue),
    [options, currentValue],
  )
  const resolvedPlaceholder = placeholder ?? t('select.placeholder')

  const control = (
    <Popover
      align={align}
      side="auto"
      portal={portal}
      wrapperClassName="w-full"
      contentClassName="min-w-full"
      onOpenChange={onOpenChange}
      trigger={(open) => (
        <SelectTrigger
          open={open}
          disabled={disabled}
          error={error}
          selectSize={selectSize}
          className={className}
        >
          <SelectTriggerContent
            selectedOption={selectedOption}
            placeholder={resolvedPlaceholder}
          />
        </SelectTrigger>
      )}
    >
      {(close) => (
        <SelectPanel
          options={options}
          selectedValue={currentValue}
          loading={loading}
          contentProps={contentProps}
          onSelect={(option) => {
            setValue(option.value)
            close()
          }}
        />
      )}
    </Popover>
  )

  if (!label) return control
  return (
    <div
      className={cn(
        'flex w-full min-w-0 flex-col',
        labelSize === 'sm' ? 'gap-0.5' : 'gap-1',
      )}
    >
      <Label htmlFor={id} size={labelSize}>
        {label}
      </Label>
      {control}
    </div>
  )
}
